import React, { useEffect, useMemo, useRef, useState } from 'react'
import Select from "react-select"
import { Button, Col, Form, InputGroup, Offcanvas, Row } from 'react-bootstrap'
import { Controller, useForm } from 'react-hook-form'
import { IconCircleX, IconPlus, IconTrash } from '@tabler/icons-react';
import PhoneInput from 'react-phone-input-2';
import z from 'zod';
import TimezoneSelect, { allTimezones } from 'react-timezone-select';
import TagsInput from 'react-tagsinput';
import useLocalStorageState from 'use-local-storage-state';
import { toast } from 'sonner';
import { zodResolver } from '@hookform/resolvers/zod';
import validator from "validator"
import { components } from "react-select"
import { useNavigate } from 'react-router-dom';
import { parsePhoneNumber } from "libphonenumber-js/min"
import { checkDuplicate, customStyles, useDebounce } from '../../../../Utils/helpers/commonhelper';
import { UpdateRF, GetRequest } from '../../../../Utils/hooks/ApiHandler';
import { DateField, MultipleOpt, PhoneNumberInput, SelectOpt, Text, Time } from '../../../../pages/FieldComp';
import { getCities, getStates } from '../../../../Utils/hooks/location';
import { usePostRequest } from '../../../../Utils/hooks/QueryHandler';
import { UsersPopup } from '../../../../pages/UsersPopup';
import { CreateCompany } from '../../../../pages/CreateCompany';
import { ContactFormCommonOffcanvas } from './ContactFormCommonOffcanvas';

const optionalUrl = (label) =>
    z
        .string()
        .trim()
        .optional()
        .refine(
            (val) => !val || !val.includes(" "),
            `${label} URL cannot contain spaces`
        )
        .refine(
            (val) => !val || /^https?:\/\/.+/.test(val),
            `Invalid ${label} URL`
        );

const contactSchema = z.object({
    salutation: z.string().default("Mr."),
    first_name: z.string().min(1, "Required"),
    last_name: z.string(),
    owner_id: z.string().optional(),

    visible_to: z
        .array(
            z.object({
                id: z.string(),
                label: z.string(),
                type: z.enum(["user", "team", "all"]),
            })
        )
        .nonempty("Required !"),

    report_to: z.string().optional(),
    address: z.string().optional(),
    countryId: z.string().optional(),
    state: z.string().optional(),
    city: z.string().optional(),
    zipcode: z.string().optional(),

    facebook: optionalUrl("Facebook"),
    twitter: optionalUrl("Twitter"),
    instagram: optionalUrl("Instagram"),
    linkedin: optionalUrl("LinkedIn"),

    company_id: z.string().optional(),
    department: z.string().optional(),
    designation: z.string().optional(),
    lead_source_id: z.string().min(1, "Lead Source is required!"),
    note: z.string().optional(),
});

export const ContactForm = ({
    show,
    setShow,
    companyGrants,
    refetch,
    country,
    allContacts,
    masterGrant,

    // extra props for other modules to be declared below
    setRecentContactId,
    onClose,
    companyId
    // all the functions that are supposed to be called when the contact form opens or loads. This would be called singletime
    // perhaps i need to change it in future if any of the function needs a refetch
    // callbackFunctions,

}) => {

    const [customer, setCustomer] = useLocalStorageState('customer')
    const navigate = useNavigate()
    const headers = {
        Authorization: customer?.token?.authorization,
        Authentication: customer?.token?.authentication,
    }
    // required fields state
    const [showRequired, setShowRequired] = useState(true)
    // Helper function to check if field should be shown
    const shouldShowField = (field) => {
        if (!showRequired) return true; // Show all fields when toggle is off

        // When toggle is on, show only fields that are required OR important
        return field.is_required === "yes" || field.is_important === "yes";
    };

    // contacts
    const [contacts, setContacts] = useState([
        { phone: "", phone_type: "Mobile" },
    ])
    const phoneOptions = [
        { value: "Mobile", label: "Mobile" },
        { value: "Work", label: "Work" },
        { value: "Home", label: "Home" },
        { value: "Personal", label: "Personal" },
    ]

    // ref for visible to field
    const visibleToRef = useRef(null)
    // lead source section ref
    const leadSourceRef = useRef(null)

    // emails
    const [emails, setEmails] = useState([{ email: "", email_type: "Office" }])
    const emailOptions = [
        { value: "Office", label: "Office" },
        { value: "Personal", label: "Personal" },
    ]
    // states for empty emails and contact
    const [emptyEmailIndices, setEmptyEmailIndices] = useState([])
    const [emptyContactIndices, setEmptyContactIndices] = useState([])
    // refs for scrolling
    const emailSectionRef = useRef(null)
    const contactSectionRef = useRef(null)
    // duplicacy with respect to the primary email and contact
    const [duplicateEmailMessage, setDuplicateEmailMessage] = useState(null)
    const [duplicatePhoneMessage, setDuplicatePhoneMessage] = useState(null)
    const debouncedEmail = useDebounce(emails[0]?.email || "", 500)
    const debouncedPhone = useDebounce(contacts[0]?.phone || "", 500)

    // states state
    const [states, setStates] = useState([])
    // city state
    const [city, setCity] = useState([])

    // timezone
    const [selectedTimezone, setSelectedTimezone] = useState({
        value: "Asia/Kolkata",
        label: "(GMT+5:30) Chennai, Kolkata, Mumbai, New Delhi",
        offset: 5.5,
        abbrev: "IST",
        altName: "India Standard Time",
    })

    // tags
    const [tags, setTags] = useState([])
    // for tracking if the tags are entered or not 
    // const [isTagEmpty, setIsTagEmpty] = useState(false)
    // image state
    const [image, setImage] = useState()

    // decision maker state
    const [decision, setDecision] = useState(false)

    // lead owner state
    const [leadOwner, setLeadOwner] = useState([
        {
            leadName: "",
            leadId: "",
        },
    ])
    // set team name and id for showing in visible to field in add contact form
    const [teams, setTeams] = useState([
        {
            teamName: "",
            teamId: "",
        },
    ])
    // lead source state
    const [lead_source, setLeadSource] = useState([])

    // state for companies
    const [company, setCompany] = useState([])
    // autoselect state
    const [recentCompany, setRecentCompanyId] = useState({
        recentCompanyId: "",
        recentCompanyName: "",
    })
    const [recentSource, setRecentSource] = useState({
        recentSourceId: "",
        recentSourceName: "",
    })
    // state to open the lead source form
    const [sourceShow, setSourceShow] = useState(false)
    // company canvas state
    const [companyShow, setCompanyShow] = useState(false)

    //End column visibility setting
    // function to set the dynamic fields for the dynamic fields
    const generateDynamicSchema = (fields) => {
        const schemaFields = {}

        fields.forEach((field) => {
            const fieldId = field.id

            switch (field.label_type) {
                case "text":
                case "largeText":
                case "address":
                    schemaFields[fieldId] =
                        field.is_required === "yes"
                            ? z.string().min(1, `${field.label_name} is required`)
                            : z.string().optional()
                    break

                case "numerical":
                    schemaFields[fieldId] =
                        field.is_required === "yes"
                            ? z.string().min(1, `${field.label_name} is required`)
                            : z.string().optional()
                    break

                // case "monetary":
                //   schemaFields[fieldId] =
                //     field.is_required === "yes"
                //       ? z.number().min(0, "Amount must be positive")
                //       : z.number().optional().nullable().or(z.literal(""));
                //   break

                case "monetary":
                    schemaFields[fieldId] = field.is_required === "yes"
                        ? z.preprocess(
                            (val) => (val === "" || val === null || val === undefined ? undefined : Number(val)),
                            z.number({
                                required_error: `${field.label_name} is required`,
                                invalid_type_error: "Please enter a valid number"
                            })
                                .min(0.01, "Amount must be positive")
                        )
                        : z.preprocess(
                            (val) => (val === "" || val === null || val === undefined ? null : Number(val)),
                            z.number().min(0, "Amount cannot be negative").optional().nullable()
                        );
                    break;

                case "single-option":
                case "user":
                case "person":
                case "organization":
                    schemaFields[fieldId] =
                        field.is_required === "yes"
                            ? z.string().min(1, `${field.label_name} is required`)
                            : z.string().optional().nullable().or(z.literal(""));
                    break;

                case "date":
                    schemaFields[fieldId] =
                        field.is_required === "yes"
                            ? z.string().min(1, `${field.label_name} is required`)
                            : z.string().optional()
                    break

                case "date range":
                    schemaFields[fieldId] =
                        field.is_required === "yes"
                            ? z
                                .object({
                                    start: z.string().min(1, "Start date is required"),
                                    end: z.string().min(1, "End date is required"),
                                })
                                .refine((data) => data.start <= data.end, {
                                    message: "End date must be after start date",
                                })
                            : z
                                .object({
                                    start: z.string(),
                                    end: z.string(),
                                })
                                .optional()
                    break

                case "time":
                    schemaFields[fieldId] =
                        field.is_required === "yes"
                            ? z.string().min(1, `${field.label_name} is required`)
                            : z.string().optional()
                    break

                case "time-range":
                    schemaFields[fieldId] =
                        field.is_required === "yes"
                            ? z.object({
                                start: z.string().min(1, "Start time is required"),
                                end: z.string().min(1, "End time is required"),
                            })
                            : z
                                .object({
                                    start: z.string(),
                                    end: z.string(),
                                })
                                .optional()
                    break

                case "phone":
                    schemaFields[fieldId] =
                        field.is_required === "yes"
                            ? z.string(z.string().min(1, "phone is requ"))
                            : z.string().optional()
                    break
                case "multiple-option": // Added multiple-option schema
                    schemaFields[fieldId] = // Changed from fieldName
                        field.is_required === "yes"
                            ? z.array(z.string()).min(1, `${field.label_name} is required`)
                            : z.array(z.string()).optional()
                    break

                default:
                    schemaFields[fieldId] = z.any().optional()
            }
        })

        return z.object(schemaFields)
    }

    // groped state to hold the dynamic group data
    const [grouped, setGrouped] = useState([])
    // ungroped state to hold the dynamic group data
    const [ungrouped, setUnGrouped] = useState([])
    // state to store all dynamic fields
    const [dynamicFields, setDynamicFields] = useState([])

    // Memoize the combined schema to avoid unnecessary recalculations
    const finalSchema = useMemo(() => {
        if (dynamicFields.length === 0) {
            return contactSchema
        }

        // Combine grouped and ungrouped fields
        const allDynamicFields = [
            ...(grouped?.flatMap((g) => g.groupFields || []) || []),
            ...(ungrouped || []),
        ]

        if (allDynamicFields.length === 0) {
            return contactSchema
        }

        const dynamicSchema = generateDynamicSchema(allDynamicFields)

        // Merge static and dynamic schemas
        return contactSchema.merge(dynamicSchema)
    }, [grouped, ungrouped, dynamicFields])

    // form methods destructuring
    const {
        handleSubmit,
        control,
        register,
        reset,
        setValue,
        watch,
        formState: { errors, isValid, isDirty },
    } = useForm({
        resolver: zodResolver(finalSchema),
        owner_id: customer?.customer_details?.id,
        mode: "onChange",
    })

    // to validate if phone input is correct...
    const validatePhoneNumber = (rawNumber, countryCode) => {
        try {
            const phoneNumber = parsePhoneNumber(
                String(rawNumber),
                String(countryCode?.toUpperCase())
            )
            const valid = phoneNumber?.isValid() || false
            return valid
        } catch (error) {
            return false
        }
    }

    // useEffect for email duplication check
    useEffect(() => {
        const checkEmail = async () => {
            if (debouncedEmail) {
                setDuplicateEmailMessage(null)
                const isEmailExisting = await checkDuplicate({
                    col: "email",
                    customer_id: customer.customer_details?.customer_id, // Hardcoded as per example
                    validating_val: debouncedEmail,
                    contact_id: "", // Hardcoded as per example
                    checkFor: "contacts",
                })
                if (isEmailExisting) {
                    setDuplicateEmailMessage("Email already exists!")
                }
            } else {
                setDuplicateEmailMessage(null)
            }
        }
        checkEmail()
    }, [debouncedEmail])

    // useEffect for phone duplication check
    useEffect(() => {
        const checkPhone = async () => {
            if (debouncedPhone) {
                setDuplicatePhoneMessage(null)
                const isPhoneExisting = await checkDuplicate({
                    col: "phone",
                    customer_id: customer.customer_details?.customer_id,
                    validating_val: debouncedPhone,
                    contact_id: "",
                    checkFor: "contacts",
                })
                if (isPhoneExisting) {
                    setDuplicatePhoneMessage("Phone number already exists!")
                }
            } else {
                setDuplicatePhoneMessage(null)
            }
        }
        checkPhone()
    }, [debouncedPhone])

    // useeffect for state
    const countryId = watch("countryId")
    useEffect(() => {
        ; (async () => {
            try {
                const data = await getStates(countryId ? countryId : 99)
                if (!data || data === undefined) {
                    setStates([])
                    setValue("state", "")
                    return
                }
                // // Update state once with all country data
                setStates(
                    data?.map((state) => ({
                        stateName: state.state_name,
                        stateCode: state.id,
                    }))
                )
                setValue("state", "")
            } catch (error) {
                toast.error("Error Fetching State")
                return
            }
        })()
    }, [countryId])

    // city useeffect
    const stateId = watch("state")
    useEffect(() => {
        ; (async () => {
            try {
                if (!stateId || stateId === "") {
                    setCity([])
                    setValue("city", "") // Clear city selection
                    return
                }
                const data = await getCities(stateId)
                if (!data || data === undefined) {
                    setCity([])
                    return
                }
                // // Update state once with all country data
                setCity(
                    data?.map((city) => ({
                        cityName: city.city_name,
                        cityCode: city.id,
                    }))
                )
                setValue("city", "")
            } catch (error) {
                toast.error("Error Fetching State")
                return
            }
        })()
    }, [stateId])

    // useeffect for lead source
    useEffect(() => {
        ; (async () => {
            const res = await GetRequest(
                `masters/getAllLead_source/${customer.customer_details?.customer_id}`,
                { headers },
                { customer, setCustomer, rf: headers.Authentication, navigate }
            )
            if (!res) return
            if (res.success === false) {
                return
            }
            setLeadSource(
                res.data?.map((lead) => ({
                    source_title: lead.source_title,
                    lead_source_id: lead.id,
                }))
            )
            res.data &&
                res.data.find((c) => {
                    if (c.id === String(recentSource.recentSourceId)) {
                        setValue("lead_source_id", recentSource.recentSourceId)
                        setRecentSource((prev) => ({
                            ...prev,
                            recentSourceName: c.source_title,
                        }))
                    }
                })
        })()
    }, [recentSource.recentSourceId])
    // useeffect for companies
    useEffect(() => {
        if (companyId) {
            setValue("company_id", companyId)
        }
        ; (async () => {
            const res = await GetRequest(
                `company/fetchCompanies/${customer.customer_details?.customer_id}`,
                { headers },
                { customer, setCustomer, rf: headers.Authentication, navigate }
            )
            if (!res) return
            if (res.success === false) {
                return
            }
            setCompany(
                res.data?.map((c) => ({
                    company_name: c.company_name,
                    company_id: c.id,
                }))
            )
            // setting the default value for the recent company being addded
            res.data &&
                res.data.find((c) => {
                    if (c.id === recentCompany.recentCompanyId) {
                        setValue("company_id", recentCompany.recentCompanyId)
                        setRecentCompanyId((prev) => ({
                            ...prev,
                            recentCompanyName: c.company_name,
                        }))
                    }
                })
        })()
        return () => { }
    }, [recentCompany.recentCompanyId])

    // fetching dynamic fields for the contact form
    useEffect(() => {
        ; (async () => {
            try {
                const res = await GetRequest(
                    `contacts/fetchCustomFields/${customer.customer_details?.customer_id}`,
                    { headers },
                    { customer, setCustomer, rf: headers.Authentication, navigate }
                )
                if (res.success === false || res.success === "fail") {
                    toast.error(res?.message)
                    return
                }
                res.data?.grouped != {} &&
                    setGrouped(res.data?.grouped && res.data?.grouped)
                res.data?.ungrouped != {} &&
                    setUnGrouped(res.data?.ungrouped && res.data?.ungrouped)
                // Update dynamic fields state to trigger schema recalculation
                const allFields = [
                    ...(res.data?.grouped?.flatMap((g) => g.groupFields || []) || []),
                    ...(res.data?.ungrouped || []),
                ]
                setDynamicFields(allFields)
            } catch (error) {
                toast.error("Internal Server Error")
                return
            }
        })()
        return () => { }
    }, [customer])

    // useeffect for fetching users for lead_owners, visible_to, report_to
    const fetchLeadOwners = async () => {
        const res = await GetRequest(
            `users/users/fetchUserAsOwner/${customer.customer_details?.customer_id}`,
            { headers },
            { customer, setCustomer, rf: headers.Authentication, navigate }
        )
        if (res.err) {
            toast.error("Error Fetching Data")
        }
        const owners =
            res.data &&
            res.data?.map((lead) => ({
                leadName: lead.full_name,
                leadId: lead.id,
            }))
        setLeadOwner(owners)

        if (customer?.customer_details?.id) {
            setValue("owner_id", customer.customer_details.id);
        }
    }

    // useEffetct fot fetching teams data
    const fetchTeamsData = async () => {
        const res = await GetRequest(
            `/users/users/fetchAllTeams/${customer.customer_details?.customer_id}`,
            { headers },
            { customer, setCustomer, rf: headers.Authentication, navigate }
        )
        if (res.err) {
            toast.error("Error Fetching Data")
        }
        const teamsData =
            res.data &&
            res.data?.map((team) => ({
                teamName: team.team_name,
                teamId: team.id,
            }))
        setTeams(teamsData)

        if (teams && teams.length > 0) {
            setValue("owner_id", customer.customer_details?.id)
        }
    }

    // running all requests parallely
    const initData = async () => {
        await Promise.all([
            fetchLeadOwners(),
            fetchTeamsData(),
        ])
    }
    useEffect(() => {
        initData()
        return () => { }
    }, [])

    // add email field handler
    const handleAddEmail = () => {
        setEmails([
            ...emails,
            {
                email: "",
                email_type: "Office",
            },
        ])
    }

    // add contact field handler
    const handleAddContact = () => {
        setContacts([
            ...contacts,
            {
                phone: "",
                phone_type: "Mobile",
            },
        ])
    }

    // delete email handler
    const handleDeleteEmail = (index) => {
        const newEmails = emails.filter((_, i) => i !== index)
        setEmails(newEmails)
        // Also remove from emptyEmailIndices if it was there
        setEmptyEmailIndices((prev) =>
            prev.filter((i) => i !== index)?.map((i) => (i > index ? i - 1 : i))
        )
    }

    // delete contact handler
    const handleDeleteContact = (index) => {
        const newContacts = contacts.filter((_, i) => i !== index)
        setContacts(newContacts)
        // Also remove from emptyContactIndices if it was there
        setEmptyContactIndices((prev) =>
            prev.filter((i) => i !== index)?.map((i) => (i > index ? i - 1 : i))
        )
    }

    // adding email inputs to email state array
    const handleEmailChange = (field, value, index) => {
        const newEmails = [...emails]
        newEmails[index] = { ...newEmails[index], [field]: value }
        setEmails(newEmails)
        // If the email field is no longer empty, remove its index from emptyEmailIndices
        if (field === "email" && value.trim() !== "") {
            setEmptyEmailIndices((prev) => prev.filter((i) => i !== index))
        }
    }

    // adding contact inputs to contact state array
    const handleContactChange = (field, value, index, details) => {
        const newContacts = [...contacts]
        newContacts[index] = { ...newContacts[index], [field]: value, ...details }
        setContacts(newContacts)
        // If the phone field is no longer empty, remove its index from emptyContactIndices
        if (field === "phone" && value.trim() !== "") {
            setEmptyContactIndices((prev) => prev.filter((i) => i !== index))
        }
    }


    // tags handler
    const handleTagChange = (newTags) => {
        const uniqueTags = []

        newTags.forEach((tag) => {
            const lowerTag = tag.toLowerCase()

            const isDuplicate = uniqueTags.some((t) => t.toLowerCase() === lowerTag)
            if (!isDuplicate) {
                uniqueTags.push(tag)
            }
        })

        setTags(uniqueTags)
    }

    // handleclose to close the offcanvas for contacts
    const handleClose = () => {
        reset()
        // setIsTagEmpty(false)
        setEmails([{ email: "", email_type: "Office" }])
        setContacts([{ phone: "", phone_type: "Mobile" }])
        setSelectedTimezone({
            value: "Asia/Kolkata",
            label: "(GMT+5:30) Chennai, Kolkata, Mumbai, New Delhi",
            offset: 5.5,
            abbrev: "IST",
            altName: "India Standard Time",
        })
        setTags([])
        setValue("owner_id", customer?.customer_details?.id)
        setEmptyContactIndices([])
        setEmptyEmailIndices([])
        setDecision(false)
        setShow(false)

        // at the end executing the functions related to other modules
        onClose && onClose()
    }

    // react query handler for submitting data
    const { mutate } = usePostRequest(
        "/contacts/createContact",
        true,
        { headers },
        { customer, setCustomer, rf: headers.Authentication, navigate }
    )

    const handleFormSubmit = async (formData) => {
        if (!isValid) return
        // Checking empty email states and contact states before form sub mitting
        const newEmptyEmailIndices = emails.reduce((acc, email, index) => {
            if (!email.email || email.email.trim() === "") {
                acc.push(index)
            }
            return acc
        }, [])
        const newEmptyContactIndices = contacts.reduce((acc, contact, index) => {
            if (!contact.phone || contact.phone.trim() === "") {
                acc.push(index)
            }
            return acc
        }, [])
        setEmptyEmailIndices(newEmptyEmailIndices)
        setEmptyContactIndices(newEmptyContactIndices)
        // scrolling if the email or contact input is empty
        if (newEmptyEmailIndices.length > 0 || newEmptyContactIndices.length > 0) {
            if (newEmptyEmailIndices.length > 0) {
                emailSectionRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                })
            } else if (newEmptyContactIndices.length > 0) {
                contactSectionRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                })
            }
            return
        }
        // Validate email formats
        const hasInvalidEmailFormat = emails.some(
            (email) => email.email.trim() !== "" && !validator.isEmail(email.email)
        )
        if (hasInvalidEmailFormat) {
            emailSectionRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
            })
            return
        }
        // Validate phone formats
        const hasInvalidContactFormat = contacts.some(
            (contact) =>
                contact.phone.trim() !== "" &&
                !validatePhoneNumber(contact.phone, contact?.countryName)
        )
        if (hasInvalidContactFormat) {
            contactSectionRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
            })
            return
        }

        const custom_fields = []
        const staticFieldsPayload = { ...formData } // Start with all form data

        dynamicFields.forEach((field) => {
            const fieldName = field.id
            if (fieldName && formData.hasOwnProperty(fieldName)) {
                let formattedValue = formData[fieldName]
                let isRangeField = false // Initialize flag for range property

                // Handle complex types for custom_fields payload
                if (
                    field.label_type === "date range" ||
                    field.label_type === "time-range"
                ) {
                    isRangeField = true // Set flag for range fields
                    // Ensure both start and end are present before sending
                    if (
                        !formattedValue ||
                        (!formattedValue.start && !formattedValue.end)
                    ) {
                        formattedValue = { start: "", end: "" } // Send empty object if range is incomplete/empty
                    }
                } else if (field.label_type === "multiple-option") {
                    // Ensure it's an array
                    if (!Array.isArray(formattedValue)) {
                        formattedValue = [] // Send empty array if not an array
                    }
                }
                const customFieldEntry = {
                    field_id: field.id.toString(), // Ensure ID is a string
                    value: formattedValue || "",
                    range: isRangeField ? true : false,
                }
                custom_fields.push(customFieldEntry)
                // Remove dynamic field from static payload to avoid duplication
                delete staticFieldsPayload[fieldName]
            }
        })
        // final data
        const dataToSend = {
            ...formData,
            country: formData.countryId || "99",
            customer_id: customer.customer_details?.customer_id,
            user_id: customer.customer_details.id,
            decision_maker: !decision ? "No" : "Yes",
            lead_source_id: formData.lead_source_id,
            email: emails[0].email,
            phone: contacts[0].phone,
            user_email: emails,
            user_phone: contacts,
            tag: tags.length < 1 ? [] : tags,
            timezone: selectedTimezone,
            contact_image: image?.[0] || null,
            report_to: formData.report_to,
        }

        // 🔵 Normalize visible_to for payload
        const normalizeVisibleToForPayload = (arr) => {
            const a = Array.isArray(arr) ? arr : []
            const pickedAll = a.find((x) => x.type === "all" || x.id === "0")

            if (pickedAll) {
                return [{ id: "0", label: "All", type: "all" }] // exact shape you wanted
            }

            return a?.map((opt) => ({
                id: opt.id.toString(),
                label: opt.label,
                type: opt.type,
            }))
        }

        const finalPayload = {
            ...dataToSend,
            custom_fields,
            visible_to: normalizeVisibleToForPayload(formData?.visible_to),
        }

        mutate(finalPayload, {
            onSuccess: (data) => {
                if (data.status === "fail") {
                    toast.error(data.message)
                    return
                }
                if (data.status === "error") return
                if (data.status === "Expire") {
                    UpdateRF(headers.Authentication)
                    navigate("/login")
                    return
                }
                refetch && refetch()
                setRecentContactId &&
                    setRecentContactId((prev) => ({
                        ...prev,
                        recentContactId: String(data.data),
                    }));
                // reset({ owner_id: customer.customer_details.id })
                reset()
                setEmails([{}])
                setContacts([{}])
                setEmptyContactIndices([])
                setEmptyEmailIndices([])
                setSelectedTimezone({
                    value: "Asia/Kolkata",
                    label: "(GMT+5:30) Chennai, Kolkata, Mumbai, New Delhi",
                    offset: 5.5,
                    abbrev: "IST",
                    altName: "India Standard Time",
                })
                setTags([])
                setValue("owner_id", customer?.customer_details?.id)
                setDecision(false)
                setShow(false)
                onClose && onClose()
                toast.success(data.message)
            },
        })
    }
    return (
        <>
            {/* Create Contact Offcanvas */}
            <ContactFormCommonOffcanvas
                formTitle={"Create Contact"}
                show={show}
                setShow={setShow}
                handleClose={handleClose}
                showRequired={showRequired}
                setShowRequired={setShowRequired}

                // handler from react hook form
                handleSubmit={handleSubmit}
                register={register}
                control={control}
                errors={errors}
                watch={watch}
                setValue={setValue}

                // handler for actual form submit
                handleFormSubmit={handleFormSubmit}
                duplicateEmailMessage={duplicateEmailMessage}
                duplicatePhoneMessage={duplicatePhoneMessage}

                emails={emails}
                handleAddContact={handleAddContact}
                handleAddEmail={handleAddEmail}
                handleDeleteContact={handleDeleteContact}
                handleDeleteEmail={handleDeleteEmail}
                handleEmailChange={handleEmailChange}
                handleContactChange={handleContactChange}

                phoneOptions={phoneOptions}
                emailOptions={emailOptions}
                emptyEmailIndices={emptyEmailIndices}
                emptyContactIndices={emptyContactIndices}
                emailSectionRef={emailSectionRef}
                visibleToRef={visibleToRef}
                leadSourceRef={leadSourceRef}
                contactSectionRef={contactSectionRef}

                company={company}
                contacts={contacts}
                leadOwner={leadOwner}
                country={country}
                states={states}
                city={city}
                teams={teams}
                ungrouped={ungrouped}
                grouped={grouped}
                lead_source={lead_source}

                validatePhoneNumber={validatePhoneNumber}
                companyShow={companyShow}
                setCompanyShow={setCompanyShow}
                decision={decision}
                setDecision={setDecision}
                selectedTimezone={selectedTimezone}
                setSelectedTimezone={setSelectedTimezone}
                sourceShow={sourceShow}
                setSourceShow={setSourceShow}

                tags={tags}
                shouldShowField={shouldShowField}
                allContacts={allContacts}
                masterGrant={masterGrant}
                companyGrants={companyGrants}

                handleTagChange={handleTagChange}
            />
            <CreateCompany
                show={companyShow}
                setShow={setCompanyShow}
                contactsModule={true}
                setRecentCompanyId={setRecentCompanyId}
                setContactShow={() => setShow(true)}
            ></CreateCompany>
            <UsersPopup
                setRecentSourceId={setRecentSource}
                sourceShow={sourceShow}
                setSourceShow={setSourceShow}
            />
        </>
    )
}